import { Routes } from '@angular/router';
import { PERMISSION_KEYS, ROUTE } from '@shared/constants';
import { publicShellScopeGuard } from '@layouts/public-layout/public-shell.guard';
import { authGuard } from '@core/auth/guards/auth.guard';
import { superAdminGuard } from '@core/auth/guards/super-admin.guard';
import { permissionGuard } from '@core/auth/guards/permission.guard';
import { withUnsavedChangesGuard } from '@core/guards';
import { kioskPwaScopeGuard } from '@modules/kiosk-device/guards/kiosk-pwa-scope.guard';

/**
 * Root routing table. Three shells live here and nothing else:
 *
 * | Shell          | Mounted at | Chrome                       |
 * |----------------|------------|------------------------------|
 * | chrome-less    | various    | none — kiosk, auth, run/view |
 * | `PublicLayout` | `''`       | top bar + footer, no sidebar |
 * | `MainLayout`   | `''`       | full app shell               |
 *
 * Every feature is reached with `loadChildren` into a **module** route file
 * (`modules/<module>/<module>.routes.ts`), which in turn lazy-loads each **submodule**
 * (`modules/<module>/<feature>/<feature>.routes.ts`). Add a screen by editing its module file —
 * this one only changes when a new top-level URL segment or shell is introduced.
 *
 * Permission guards sit on the entry that owns the URL prefix, so a guard runs *before* its chunk
 * is fetched and an unauthorised user never downloads the code.
 */
const APP_ROUTES_DEF: Routes = [
  {
    // Full-screen kiosk-device appliance UI — authenticates itself via its own device token, not
    // the app session, so it deliberately has no authGuard. No canMatch here either: this IS the
    // /kiosk destination the guard below redirects to.
    path: ROUTE.KIOSK.segment,
    title: 'Kiosk',
    loadChildren: () =>
      import('@modules/kiosk-device/kiosk-device.routes').then((m) => m.KIOSK_DEVICE_ROUTES),
  },
  {
    path: ROUTE.LOGIN.segment,
    title: 'Sign in',
    canMatch: [kioskPwaScopeGuard],
    loadChildren: () => import('@modules/auth/login/login.routes').then((m) => m.LOGIN_ROUTES),
  },
  {
    path: ROUTE.WELCOME.segment,
    title: 'Welcome',
    canMatch: [kioskPwaScopeGuard],
    loadChildren: () =>
      import('@modules/auth/welcome/welcome.routes').then((m) => m.WELCOME_ROUTES),
  },
  {
    // Full-screen authenticated document runner — OUTSIDE the app shell.
    path: ROUTE.RUN.segment,
    canActivate: [authGuard],
    loadChildren: () =>
      import('@modules/pms/documents/documents.routes').then((m) => m.DOCUMENT_RUN_ROUTES),
  },
  {
    // Full-screen authenticated manager PREVIEW (test-run) — OUTSIDE the app shell. Same runner as /run,
    // on its own route so a preview never mixes with a real assigned run and persists nothing.
    path: ROUTE.PREVIEW.segment,
    canActivate: [authGuard],
    loadChildren: () =>
      import('@modules/pms/documents/documents.routes').then((m) => m.DOCUMENT_PREVIEW_ROUTES),
  },
  {
    // Same readers as /preview, but opened TO ACKNOWLEDGE — its own prefix so a "read this" link and a
    // "sign this" link are never the same URL, and only the latter shows the sign-off box.
    path: ROUTE.SIGNOFF.segment,
    canActivate: [authGuard],
    loadChildren: () =>
      import('@modules/pms/documents/documents.routes').then((m) => m.DOCUMENT_SIGNOFF_ROUTES),
  },
  {
    // Full-screen authenticated read-only document view — OUTSIDE the app shell.
    path: ROUTE.VIEW.segment,
    canActivate: [authGuard],
    loadChildren: () =>
      import('@modules/pms/documents/documents.routes').then((m) => m.DOCUMENT_VIEW_ROUTES),
  },
  {
    // Full-view public shell — top bar + footer only, no sidebar. Everything meant to be READ rather
    // than operated lives here, so a published help-centre page and an author's draft preview both
    // open at full width and look identical to the reader.
    //
    // Declared before the app-shell route below — both are empty-path, so `publicShellScopeGuard`
    // decides which shell a URL belongs to rather than leaving it to the router to backtrack.
    path: '',
    loadComponent: () =>
      import('@layouts/public-layout/public-layout.component').then((m) => m.PublicLayoutComponent),
    canMatch: [kioskPwaScopeGuard, publicShellScopeGuard],
    children: [
      {
        // The real, API-backed help centre. Ungated on purpose: the API scopes every read to the
        // articles the caller is cleared for, so a thin role sees fewer articles rather than a 403.
        path: ROUTE.SUPPORT_HELP.segment,
        title: 'Help centre',
        canActivate: [authGuard],
        loadChildren: () =>
          import('@modules/support/help/help.routes').then((m) => m.SUPPORT_HELP_ROUTES),
      },
      {
        // Retired reader mount. `/docs` was served by the localStorage demo, and the server emitted
        // `/docs/<category>/<slug>` links before `SupportPaths.Reader` moved to `/help` — so the
        // segment stays claimed and forwards rather than 404ing. A prefix redirect carries the
        // remaining segments over, so a link shared from the old reader lands on the same article.
        path: ROUTE.HELP_CENTRE.segment,
        redirectTo: ROUTE.SUPPORT_HELP.path,
      },
      {
        // The static pages (privacy, terms, …) sit at the root of this shell, so they are matched
        // last — after 'docs' has had its turn at the first segment.
        path: '',
        loadChildren: () =>
          import('@modules/public-pages/public-pages.routes').then((m) => m.PUBLIC_PAGES_ROUTES),
      },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('@layouts/main-layout/main-layout.component').then((m) => m.MainLayoutComponent),
    canActivate: [authGuard],
    canMatch: [kioskPwaScopeGuard],
    children: [
      { path: '', redirectTo: ROUTE.DASHBOARD.segment, pathMatch: 'full' },
      {
        path: ROUTE.DASHBOARD.segment,
        title: 'Dashboard',
        loadChildren: () =>
          import('@modules/dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES),
      },
      {
        // No title here: Profile is in the sidebar now, so AppTitleStrategy takes its name from the
        // nav leaf — a route title would shadow it and the tab would disagree with the menu.
        path: ROUTE.PROFILE.segment,
        loadChildren: () => import('@modules/profile/profile.routes').then((m) => m.PROFILE_ROUTES),
      },
      {
        // Top-level Notifications module — the full in-app notification inbox (any authenticated user).
        path: ROUTE.NOTIFICATIONS.segment,
        title: 'Notifications',
        loadChildren: () =>
          import('@modules/notifications/notifications.routes').then((m) => m.NOTIFICATIONS_ROUTES),
      },
      {
        // Unified Tasks inbox for EVERY authenticated user (self-service; no permission guard). A plain
        // signer/assignee sees only their own runs + sign-offs; a manager (pms.tasks.view) also gets the
        // "All Tasks" view + assign/remind/revoke. Replaces the separate My Sign-offs / My Work inboxes.
        // Lives outside /pms because it is self-service, not a managed PMS surface.
        path: ROUTE.TASKS.segment,
        loadChildren: () =>
          import('@modules/pms/documents/documents.routes').then((m) => m.PMS_TASKS_ROUTES),
      },
      {
        // Read-only roster — the "Shift Schedule" solo item in the sidebar. Top level, beside the other
        // solo entries it sits with, rather than under /hrm: it is not the HRM module's roster-management
        // surface, it is the same roster with every write action withheld.
        //
        // ROSTER_VIEW is the right gate even though the page changes nothing: the roster it renders comes
        // from a controller class-gated on roster.view, so an ungated route would show the reader a page
        // and then a 403.
        path: ROUTE.SHIFT_SCHEDULE.segment,
        title: 'Shift Schedule',
        canActivate: [permissionGuard(PERMISSION_KEYS.ROSTER_VIEW)],
        loadChildren: () =>
          import('@modules/hrm/shift/shift.routes').then((m) => m.SHIFT_SCHEDULED_ROUTES),
      },
      {
        path: ROUTE.ADMIN.segment,
        canActivate: [superAdminGuard],
        loadChildren: () => import('@modules/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
      },
      {
        path: ROUTE.BMS.segment,
        loadChildren: () => import('@modules/bms/bms.routes').then((m) => m.BMS_ROUTES),
      },
      {
        path: ROUTE.COMPANY.segment,
        loadChildren: () => import('@modules/company/company.routes').then((m) => m.COMPANY_ROUTES),
      },
      {
        path: ROUTE.HRM.segment,
        loadChildren: () => import('@modules/hrm/hrm.routes').then((m) => m.HRM_ROUTES),
      },
      {
        path: ROUTE.PMS.segment,
        loadChildren: () => import('@modules/pms/pms.routes').then((m) => m.PMS_ROUTES),
      },
      {
        path: ROUTE.SUPPORT.segment,
        loadChildren: () => import('@modules/support/support.routes').then((m) => m.SUPPORT_ROUTES),
      },
      {
        path: ROUTE.SETTINGS.segment,
        loadChildren: () =>
          import('@modules/settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
      },
      {
        // Internal component/demo showcase — not a tenant feature; keep it off regular users' reach.
        path: ROUTE.DEVELOPER.segment,
        canActivate: [superAdminGuard],
        loadChildren: () =>
          import('@modules/developer/developer.routes').then((m) => m.DEVELOPER_ROUTES),
      },
      {
        path: ROUTE.FEATURE.segment,
        title: 'Coming soon',
        loadComponent: () =>
          import('@shared/components/ui/placeholder/placeholder.component').then(
            (m) => m.PlaceholderComponent
          ),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];

export const APP_ROUTES: Routes = withUnsavedChangesGuard(APP_ROUTES_DEF);
